import { z } from "zod"
import {
  FreshServiceProposal,
  ServiceProposalManualAction,
  ServiceProposalPackage,
  ServiceProposalPackageSchema,
  ServiceProposalPartAction,
  SubmitFreshServiceProposal,
  SubmitFreshServiceProposalSchema,
} from "./fresh-service-proposal"
import { PartActionUrgency } from "./part-action-urgency"
import { buildDefaultSelection } from "./proposal-default-selection"
import { ProposalLevel, ProposalServiceChoice } from "./proposal-level-kind"
import { ServiceAction } from "./service-part-action"
import {
  findProposalPartAction,
  getSelectedPartIds,
} from "./service-proposal-grouping"

/**
 * One way to apply a level: the selection a click puts in place, and the
 * packages the level shows alongside it. Packages are matched to the level's
 * components the way the optimizer does; where the proposal's own copy can
 * serve the level it is used, so the snapshot stays the one that was sent.
 * Manual actions, product decisions and Tillval are left empty: the customer's
 * own are kept.
 */
export const ProposalLevelOptionSchema = z.object({
  selection: SubmitFreshServiceProposalSchema,
  packages: z.array(ServiceProposalPackageSchema),
})
export type ProposalLevelOption = z.infer<typeof ProposalLevelOptionSchema>

/**
 * One level, worked out by the API. The only thing that can change which
 * packages win is whether the proposal's own wash is bought — so there is one
 * option with it and one without.
 */
export const ProposalLevelPresetSchema = z.object({
  /** Null when the proposal has no wash of its own. */
  withWash: ProposalLevelOptionSchema.nullable(),
  withoutWash: ProposalLevelOptionSchema,
})
export type ProposalLevelPreset = z.infer<typeof ProposalLevelPresetSchema>

/**
 * Both levels, sent with the proposal so switching between them is instant.
 */
export const ProposalLevelPresetsSchema = z.object({
  levels: z.object({
    [ProposalLevel.TRAFFIC_SAFE]: ProposalLevelPresetSchema,
    [ProposalLevel.TOP_CONDITION]: ProposalLevelPresetSchema,
  }),
})
export type ProposalLevelPresets = z.infer<typeof ProposalLevelPresetsSchema>

/** Every package either level shows, with the wash bought or not. */
export const getLevelPresetPackages = (
  presets: ProposalLevelPresets,
): ServiceProposalPackage[] =>
  Object.values(presets.levels)
    .flatMap((preset) => [preset.withWash, preset.withoutWash])
    .flatMap((option) => (option ? option.packages : []))

/**
 * The components a level works on. Work the customer cannot decline —
 * mandatory, or already approved at the reception — is in both levels.
 */
/**
 * Components a reception-approved package carries. The lock lives on the
 * package, not on the top-level copy of its actions.
 */
const getReceptionApprovedPackagePartIds = (
  proposal: FreshServiceProposal,
): Set<string> =>
  new Set(
    proposal.packages
      .filter((servicePackage) => servicePackage.isApprovedViaReception)
      .flatMap((servicePackage) =>
        servicePackage.partActions.map((action) => action.partId),
      ),
  )

export const getProposalLevelPartIds = (
  proposal: FreshServiceProposal,
  level: ProposalLevel,
): Set<string> => {
  const receptionApprovedPackagePartIds =
    getReceptionApprovedPackagePartIds(proposal)

  return new Set(
    proposal.partActions
      .filter((action) => action.action !== ServiceAction.DO_NOTHING)
      .filter(
        (action) =>
          level === ProposalLevel.TOP_CONDITION ||
          action.urgency === PartActionUrgency.NOW ||
          action.isMandatory ||
          action.isApprovedViaReception ||
          receptionApprovedPackagePartIds.has(action.partId),
      )
      .map((action) => action.partId),
  )
}

/**
 * False when there is no real choice to offer: both levels would contain the
 * same work, or Trafiksäker would hold nothing the customer could decline
 * (everything optional can wait), so it would only be the mandatory work.
 */
export const hasProposalLevels = (proposal: FreshServiceProposal): boolean => {
  const trafficSafePartIds = getProposalLevelPartIds(
    proposal,
    ProposalLevel.TRAFFIC_SAFE,
  )
  const receptionApprovedPackagePartIds =
    getReceptionApprovedPackagePartIds(proposal)
  const hasDeclinableUrgentWork = proposal.partActions.some(
    (action) =>
      trafficSafePartIds.has(action.partId) &&
      action.urgency === PartActionUrgency.NOW &&
      !action.isMandatory &&
      !action.isApprovedViaReception &&
      !receptionApprovedPackagePartIds.has(action.partId),
  )
  return (
    hasDeclinableUrgentWork &&
    trafficSafePartIds.size <
      getProposalLevelPartIds(proposal, ProposalLevel.TOP_CONDITION).size
  )
}

/**
 * The level a selection answers. Selections saved before the levels existed
 * read as Toppskick, the level everything used to be.
 */
export const getSelectionLevel = (
  values: SubmitFreshServiceProposal,
): ProposalLevel =>
  values.isCustomService === true
    ? ProposalLevel.TOP_CONDITION
    : (values.level ?? ProposalLevel.TOP_CONDITION)

/** Which of the cards on "Välj service" a selection answers. */
export const getSelectionServiceChoice = (
  values: SubmitFreshServiceProposal,
): ProposalServiceChoice => {
  if (values.isCustomService === true) return ProposalServiceChoice.CUSTOM
  return getSelectionLevel(values) === ProposalLevel.TRAFFIC_SAFE
    ? ProposalServiceChoice.TRAFFIC_SAFE
    : ProposalServiceChoice.TOP_CONDITION
}

/** The customer has saved an "Anpassad service" to go back to. */
export const hasCustomService = (values: SubmitFreshServiceProposal): boolean =>
  values.isCustomService === true ||
  (values.removedPartIds ?? []).length > 0 ||
  (values.removedManualActionIds ?? []).length > 0

/**
 * Another wash alternative than the proposal's own is never in a package, so
 * it stays standalone whatever the level picks.
 */
const getPickedAlternativeWashId = (
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): string | undefined =>
  values.selectedWashOriginalId !== undefined &&
  values.selectedWashOriginalId !== proposal.wash?.originalWashId
    ? values.selectedWashOriginalId
    : undefined

/** The wash the customer is buying picks which of a level's options applies. */
const getLevelOption = (
  preset: ProposalLevelPreset,
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): ProposalLevelOption => {
  const buysProposalWash =
    getPickedAlternativeWashId(proposal, values) === undefined &&
    (values.selectedWashOriginalId !== undefined ||
      values.selectedPackages.some(
        (selectedPackage) => selectedPackage.selectedWashOriginalId,
      ))
  return buysProposalWash && preset.withWash
    ? preset.withWash
    : preset.withoutWash
}

/**
 * Components only "Toppskick" works on: the mechanic judged they can wait, so
 * they are the preventive work on top of what makes the bike road-safe.
 */
const getPreventivePartIds = (proposal: FreshServiceProposal): Set<string> => {
  const trafficSafePartIds = getProposalLevelPartIds(
    proposal,
    ProposalLevel.TRAFFIC_SAFE,
  )
  return new Set(
    [...getProposalLevelPartIds(proposal, ProposalLevel.TOP_CONDITION)].filter(
      (partId) => !trafficSafePartIds.has(partId),
    ),
  )
}

/** One action a level holds, as its card and its "Läs mer" sheet list it. */
export type ProposalLevelWork = {
  key: string
  /** Can wait, as opposed to urgent. */
  isPreventive: boolean
} & (
  | { kind: "PART"; action: ServiceProposalPartAction }
  | { kind: "MANUAL"; action: ServiceProposalManualAction }
)

interface ProposalLevelWorkInput {
  /** The proposal as sent, with both levels' work. */
  proposal: FreshServiceProposal
  /** The proposal this level answers, from `getLevelProposal`. */
  levelProposal: FreshServiceProposal
  /** What the level buys. */
  levelValues: SubmitFreshServiceProposal
}

/**
 * Every action in a level, one by one: a package is listed as the work in it.
 * Washes and fees are not actions, and manual actions are in both levels, so
 * they count as urgent.
 */
export const getProposalLevelWork = ({
  proposal,
  levelProposal,
  levelValues,
}: ProposalLevelWorkInput): ProposalLevelWork[] => {
  const preventivePartIds = getPreventivePartIds(proposal)
  const toPartWork = (
    action: ServiceProposalPartAction,
  ): ProposalLevelWork => ({
    kind: "PART",
    key: `part-${action.partId}`,
    action,
    isPreventive: preventivePartIds.has(action.partId),
  })

  const selectedManualIds = new Set(
    levelValues.selectedManualActions.map((selected) => selected.id),
  )

  return [
    ...[...getSelectedPartIds(levelValues)]
      .flatMap((partId) => {
        const action = findProposalPartAction(levelProposal, partId)
        return action && action.action !== ServiceAction.DO_NOTHING
          ? [action]
          : []
      })
      .map(toPartWork),
    ...levelProposal.manualActions
      .filter((action) => selectedManualIds.has(action.id))
      .map(
        (action): ProposalLevelWork => ({
          kind: "MANUAL",
          key: `manual-${action.id}`,
          action,
          isPreventive: false,
        }),
      ),
  ]
}

/** A level's selection without the given components. */
const withoutParts = (
  selection: SubmitFreshServiceProposal,
  removedPartIds: ReadonlySet<string>,
): SubmitFreshServiceProposal => ({
  ...selection,
  selectedPartActions: selection.selectedPartActions.filter(
    (selected) => !removedPartIds.has(selected.partId),
  ),
  selectedPackages: selection.selectedPackages.flatMap((selectedPackage) => {
    const selectedPartActions = selectedPackage.selectedPartActions.filter(
      (selected) => !removedPartIds.has(selected.partId),
    )
    // A package with all of its work taken out is not bought.
    if (
      selectedPartActions.length === 0 &&
      selectedPackage.selectedPartActions.length > 0
    ) {
      return []
    }
    return [{ ...selectedPackage, selectedPartActions }]
  }),
})

/** The level's own selection, with what it has no say in carried over. */
const getProposalLevelPresetSelection = (
  presets: ProposalLevelPresets,
  level: ProposalLevel,
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): SubmitFreshServiceProposal => {
  const levelSelection = getLevelOption(
    presets.levels[level],
    proposal,
    values,
  ).selection

  return {
    ...levelSelection,
    selectedWashOriginalId:
      getPickedAlternativeWashId(proposal, values) ??
      levelSelection.selectedWashOriginalId,
    selectedManualActions: values.selectedManualActions,
    isWashDeselected: values.isWashDeselected,
    selectedUpsellProductIds: values.selectedUpsellProductIds,
    hasCompletedServiceStep: values.hasCompletedServiceStep,
    level,
  }
}

/**
 * The components "Anpassad service" goes without. While the customer answers
 * it, that is the Toppskick work they are not buying, plus what they took out
 * earlier; while they answer a preset, only what is remembered, since the
 * presets stay as sent. A component bought again is no longer removed.
 */
export const getRemovedPartIds = (
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): Set<string> => {
  const remembered = values.removedPartIds ?? []
  if (values.isCustomService !== true) return new Set(remembered)
  const selectedPartIds = getSelectedPartIds(values)
  const topConditionPartIds = getProposalLevelPartIds(
    proposal,
    ProposalLevel.TOP_CONDITION,
  )
  return new Set(
    [...remembered, ...topConditionPartIds].filter(
      (partId) => !selectedPartIds.has(partId),
    ),
  )
}

/**
 * The components the selection itself goes without: the presets are as sent,
 * so only "Anpassad service" takes anything out. The optimizer weighs the
 * selection against levels matched without these.
 */
export const getAppliedRemovedPartIds = (
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): Set<string> =>
  values.isCustomService === true
    ? getRemovedPartIds(proposal, values)
    : new Set()

/** The manual actions "Anpassad service" goes without, the same way. */
export const getRemovedManualActionIds = (
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): Set<string> => {
  const remembered = values.removedManualActionIds ?? []
  if (values.isCustomService !== true) return new Set(remembered)
  const selectedIds = new Set(values.selectedManualActions.map(({ id }) => id))
  return new Set(
    [...remembered, ...proposal.manualActions.map(({ id }) => id)].filter(
      (id) => !selectedIds.has(id),
    ),
  )
}

/**
 * Carries the customer's product choices over to a configuration the optimizer
 * sent back, or a level click is about to apply. The optimizer decides which packages and actions are in the
 * selection, never which product goes inside them: it answers every choice with
 * the mechanic's recommendation. Applied as-is that would overwrite an
 * alternative the customer picked a second earlier, and mark choices they never
 * opened as decided — so the incoming configuration keeps its shape, and every
 * product decision comes from what the customer had actually chosen.
 *
 * Decisions are looked up per action rather than per location, so a part moving
 * out of a package keeps the product picked for it.
 */
export const withCustomerProductDecisions = (
  configuration: SubmitFreshServiceProposal,
  previous: SubmitFreshServiceProposal,
): SubmitFreshServiceProposal => {
  const decisionsByActionId = new Map<string, Record<string, string>>()

  const remember = (
    actionId: string,
    productDecisions: Record<string, string>,
  ): void => {
    if (Object.keys(productDecisions).length === 0) return
    decisionsByActionId.set(actionId, productDecisions)
  }

  for (const action of previous.selectedPartActions) {
    remember(action.partId, action.productDecisions)
  }
  for (const action of previous.selectedManualActions) {
    remember(action.id, action.productDecisions)
  }
  for (const selectedPackage of previous.selectedPackages) {
    for (const action of selectedPackage.selectedPartActions) {
      remember(action.partId, action.productDecisions)
    }
  }

  const decisionsFor = (actionId: string): Record<string, string> =>
    decisionsByActionId.get(actionId) ?? {}

  return {
    ...configuration,
    selectedPackages: configuration.selectedPackages.map((selectedPackage) => ({
      ...selectedPackage,
      selectedPartActions: selectedPackage.selectedPartActions.map(
        (action) => ({
          ...action,
          productDecisions: decisionsFor(action.partId),
        }),
      ),
    })),
    selectedPartActions: configuration.selectedPartActions.map((action) => ({
      ...action,
      productDecisions: decisionsFor(action.partId),
    })),
    selectedManualActions: configuration.selectedManualActions.map(
      (action) => ({
        ...action,
        productDecisions: decisionsFor(action.id),
      }),
    ),
  }
}

/**
 * The selection a preset click applies: the level exactly as sent, with every
 * manual action. Everything the level has no say in is carried over, and so
 * is what "Anpassad service" goes without, so its card is still there to go
 * back to.
 */
export const getProposalLevelSelection = (
  presets: ProposalLevelPresets,
  level: ProposalLevel,
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): SubmitFreshServiceProposal =>
  // The preset answers no product choice; the customer's picks are kept so
  // the level is priced, and applied, with them.
  withCustomerProductDecisions(
    {
      ...getProposalLevelPresetSelection(presets, level, proposal, values),
      selectedManualActions: proposal.manualActions.map((action) => ({
        id: action.id,
        productDecisions: {},
      })),
      isCustomService: false,
      removedPartIds: [...getRemovedPartIds(proposal, values)],
      removedManualActionIds: [...getRemovedManualActionIds(proposal, values)],
    },
    values,
  )

/**
 * The selection "Anpassad service" stands for: Toppskick without what the
 * customer took out. `presets` should be matched without those components;
 * they are filtered here too because the presets lag a removal by one
 * optimizer run.
 */
export const getCustomServiceSelection = (
  presets: ProposalLevelPresets,
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): SubmitFreshServiceProposal => {
  const removedPartIds = getRemovedPartIds(proposal, values)
  const removedManualActionIds = getRemovedManualActionIds(proposal, values)
  return withCustomerProductDecisions(
    {
      ...withoutParts(
        getProposalLevelPresetSelection(
          presets,
          ProposalLevel.TOP_CONDITION,
          proposal,
          values,
        ),
        removedPartIds,
      ),
      selectedManualActions: proposal.manualActions
        .filter((action) => !removedManualActionIds.has(action.id))
        .map((action) => ({ id: action.id, productDecisions: {} })),
      isCustomService: true,
      removedPartIds: [...removedPartIds],
      removedManualActionIds: [...removedManualActionIds],
    },
    values,
  )
}

/** The selection one of the cards on "Välj service" applies. */
export const getServiceChoiceSelection = (
  presets: ProposalLevelPresets,
  choice: ProposalServiceChoice,
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): SubmitFreshServiceProposal => {
  switch (choice) {
    case ProposalServiceChoice.CUSTOM:
      return getCustomServiceSelection(presets, proposal, values)
    case ProposalServiceChoice.TOP_CONDITION:
      return getProposalLevelSelection(
        presets,
        ProposalLevel.TOP_CONDITION,
        proposal,
        values,
      )
    case ProposalServiceChoice.TRAFFIC_SAFE:
      return getProposalLevelSelection(
        presets,
        ProposalLevel.TRAFFIC_SAFE,
        proposal,
        values,
      )
  }
}

/**
 * What "Spara" in "Anpassa service" settles on. Work that is all of
 * Toppskick, or exactly Trafiksäker, is that preset rather than a custom
 * service of its own.
 */
export const getSavedCustomServiceChoice = (
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): ProposalServiceChoice => {
  const removedPartIds = getRemovedPartIds(proposal, {
    ...values,
    isCustomService: true,
  })
  const removedManualActionIds = getRemovedManualActionIds(proposal, {
    ...values,
    isCustomService: true,
  })
  if (removedManualActionIds.size > 0) return ProposalServiceChoice.CUSTOM
  if (removedPartIds.size === 0) return ProposalServiceChoice.TOP_CONDITION
  const keptPartIds = [
    ...getProposalLevelPartIds(proposal, ProposalLevel.TOP_CONDITION),
  ].filter((partId) => !removedPartIds.has(partId))
  const trafficSafePartIds = getProposalLevelPartIds(
    proposal,
    ProposalLevel.TRAFFIC_SAFE,
  )
  return keptPartIds.length === trafficSafePartIds.size &&
    keptPartIds.every((partId) => trafficSafePartIds.has(partId))
    ? ProposalServiceChoice.TRAFFIC_SAFE
    : ProposalServiceChoice.CUSTOM
}

/**
 * The proposal the customer is answering. Toppskick and Trafiksäker are two
 * proposals, not two selections on one: work and packages only the other
 * level has are not listed at all, so nothing reads as "Borttagen" that the
 * customer never removed.
 *
 * Packages the selection holds are always listed, wherever they came from —
 * the optimizer can swap one in, and buying the wash moves the level to its
 * other option.
 */
export const getLevelProposal = (
  proposal: FreshServiceProposal,
  presets: ProposalLevelPresets,
  values: SubmitFreshServiceProposal,
): FreshServiceProposal => {
  const level = getSelectionLevel(values)
  const preset = presets.levels[level]
  const selectedPackageIds = new Set(
    values.selectedPackages.map(
      (selectedPackage) => selectedPackage.originalPackageId,
    ),
  )
  const isSelected = (servicePackage: ServiceProposalPackage): boolean =>
    selectedPackageIds.has(servicePackage.originalPackageId)

  const packages = new Map<string, ServiceProposalPackage>()
  const candidates = [
    // Agreed at drop-off, so part of both levels.
    ...proposal.packages.filter(
      (servicePackage) => servicePackage.isApprovedViaReception,
    ),
    ...getLevelOption(preset, proposal, values).packages,
    ...[preset.withWash, preset.withoutWash]
      .flatMap((option) => (option ? option.packages : []))
      .filter(isSelected),
    ...proposal.packages.filter(isSelected),
  ]
  for (const servicePackage of candidates) {
    if (!packages.has(servicePackage.originalPackageId)) {
      packages.set(servicePackage.originalPackageId, servicePackage)
    }
  }

  const levelPartIds = getProposalLevelPartIds(proposal, level)
  return {
    ...proposal,
    packages: [...packages.values()],
    partActions: proposal.partActions.filter(
      (action) =>
        // DO_NOTHING entries only carry the mechanic's comment.
        action.action === ServiceAction.DO_NOTHING ||
        levelPartIds.has(action.partId),
    ),
  }
}

/**
 * Where an unanswered proposal starts: Toppskick unless an A/B test says
 * otherwise, with the packages that level matched. The proposal's own
 * defaults can select a package the level did not pick, which would list the
 * level's package beside it as "Borttagen".
 */
export const buildLevelDefaultSelection = (
  proposal: FreshServiceProposal,
  presets: ProposalLevelPresets | null,
  /**
   * The level to start on; an A/B test can start on Trafiksäker. Null starts
   * on none: Toppskick's work, with no level stored until the cyclist picks
   * one.
   */
  defaultLevel: ProposalLevel | null = ProposalLevel.TOP_CONDITION,
): SubmitFreshServiceProposal => {
  const defaults = buildDefaultSelection(proposal)
  if (!presets) return defaults
  const selection = getProposalLevelPresetSelection(
    presets,
    defaultLevel ?? ProposalLevel.TOP_CONDITION,
    proposal,
    defaults,
  )
  return defaultLevel === null ? { ...selection, level: undefined } : selection
}

/**
 * A saved "Anpassad service" whose work is all of Toppskick, or exactly
 * Trafiksäker, read as that preset — the same way "Spara" settles it.
 * Selections saved before the new design are all marked custom, and this
 * turns the ones the customer never actually customised back into presets.
 */
export const settleSavedCustomService = (
  proposal: FreshServiceProposal,
  values: SubmitFreshServiceProposal,
): SubmitFreshServiceProposal => {
  if (values.isCustomService !== true || !hasProposalLevels(proposal)) {
    return values
  }
  switch (getSavedCustomServiceChoice(proposal, values)) {
    case ProposalServiceChoice.CUSTOM:
      return values
    case ProposalServiceChoice.TOP_CONDITION:
      return {
        ...values,
        isCustomService: false,
        level: ProposalLevel.TOP_CONDITION,
        removedPartIds: [],
        removedManualActionIds: [],
      }
    case ProposalServiceChoice.TRAFFIC_SAFE:
      return {
        ...values,
        isCustomService: false,
        level: ProposalLevel.TRAFFIC_SAFE,
        removedPartIds: [],
        removedManualActionIds: [],
      }
  }
}
